import React, { useState, useEffect, useRef } from 'react'
import { Line } from 'react-chartjs-2'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import styled from 'styled-components'

// utils
import { getTicks } from '../../utils/getTicks'
import { getComma } from '../../utils/getComma'

// styles
import colors from '../../styles/colors'

const TEMP_DATA1 = {
  label: '주간매출1',
  data: null,
  fill: false,
  backgroundColor: 'green',
  pointHoverRadius: 2,
  pointHoverBorderWidth: 4,
  borderColor: colors.mainG,
  borderWidth: 2,
}

const TEMP_DATA2 = {
  label: '주간매출2',
  data: null,
  fill: false,
  backgroundColor: 'green',
  pointHoverRadius: 2,
  pointHoverBorderWidth: 4,
  borderColor: colors.D3,
  borderWidth: 2,
  borderDash: [3, 2],
}

const INIT_DATA = ['월', '화', '수', '목', '금', '토', '일']

const OrderChart = ({ chartItem, isMobile }) => {
  // test
  const chartRef = useRef(null)
  const [data, setData] = useState({
    labels: INIT_DATA,
    datasets: [TEMP_DATA1, TEMP_DATA2],
    // 플러그인
    plugins: [ChartDataLabels],
  })
  const [options, setOptions] = useState(null)

  // 툴팁 그림자
  const shadowPlugin = {
    id: 'shadow',
    beforeDraw: (chart) => {
      const { ctx } = chart
      const _fill = ctx.fill

      ctx.fill = function () {
        ctx.save()
        ctx.shadowColor = 'rgba(0, 0, 0, 0.1)'
        ctx.shadowBlur = 12
        ctx.shadowOffsetX = 0
        ctx.shadowOffsetY = 3
        _fill.apply(this, arguments)
        ctx.restore()
      }
    },
  }

  // 수직 라인(테스트)
  const verticalLinePlugin = {
    id: 'verticalLiner',
    afterInit: (chart, args, opts) => {
      chart.verticalLiner = {}
    },
    afterEvent: (chart, args, options) => {
      const { inChartArea } = args
      chart.verticalLiner = { draw: inChartArea }
    },
    beforeTooltipDraw: (chart, args, options) => {
      const { draw } = chart.verticalLiner
      if (!draw) return
      const { ctx } = chart
      const { top, bottom } = chart.chartArea
      const { tooltip } = args
      const x = tooltip?.caretX
      if (!x) return
      ctx.save()
      ctx.beginPath()
      ctx.moveTo(x, top)
      ctx.lineTo(x, bottom)
      ctx.strokeStyle = colors.G2
      ctx.stroke()
      ctx.restore()
    },
  }

  useEffect(() => {
    if (chartItem) {
      setData({
        ...data,

        labels: chartItem.date.slice(0, 7),
        datasets: [
          {
            label: '주간매출1',
            data: chartItem.count.slice(7, 14),
            fill: false,
            backgroundColor: 'green',
            pointHoverRadius: 2,
            pointHoverBorderWidth: 4,
            borderColor: colors.mainG,
            borderWidth: 2,
          },

          {
            label: '주간매출2',
            data: chartItem.count.slice(0, 7),
            fill: false,
            backgroundColor: 'green',
            pointHoverRadius: 2,
            pointHoverBorderWidth: 4,
            borderColor: colors.D3,
            borderWidth: 2,
            borderDash: [3, 2],
          },
        ],
      })

      setOptions({
        responsive: true,

        layout: {
          padding: {
            top: 10,
          },
        },

        elements: {
          point: {
            radius: 0,
          },
        },

        scales: {
          x: {
            grid: {
              display: false,
            },

            ticks: {
              maxTicksLimit: 7,

              font: {
                size: isMobile ? 12 : 14,
              },

              // color: colors.BK,
            },

            offset: true,

            title: {
              display: false,
              text: '날짜',
              font: {
                size: 12,
                weight: 'bold',
              },
            },
            border: {
              display: false,
            },
          },
          y: {
            grid: {
              display: true,
            },

            border: {
              display: false,
            },

            min: 0,
            max: getTicks(Math.max(...chartItem.count)).max,

            ticks: {
              stepSize: getTicks(Math.max(...chartItem.count)).stepSize,

              padding: 15,

              // color: colors.BK,

              font: {
                size: isMobile ? 12 : 14,
              },

              callback: function (value) {
                return value
              },
            },
            beginAtZero: true,
          },
        },

        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false,
            position: 'bottom',
          },
          title: {
            display: false,
            text: '날짜별 주문 지표',
          },

          tooltip: {
            backgroundColor: 'white',
            padding: 10,

            xAlign: 'left',
            yAlign: 'center',

            titleColor: 'black',
            bodySpacing: 10,
            caretSize: 0,

            titleMarginBottom: 8,

            titleFont: {
              size: 12,
              weight: 400,
            },
            bodyFont: {
              size: 10,
            },
            bodyAlign: 'left',
            footerFont: {},

            usePointStyle: true,

            callbacks: {
              title: function (item) {
                return `${item[0].label}요일`
              },
              labelColor: function (tooltipItem, data) {
                if (tooltipItem.datasetIndex === 0) {
                  return {
                    borderColor: colors.mainG,
                    backgroundColor: colors.mainG,
                  }
                } else {
                  return {
                    borderColor: '#a4a4a4',
                    backgroundColor: '#a4a4a4',
                  }
                }
              },
              labelTextColor: function (tooltipItem, data) {
                if (tooltipItem.datasetIndex === 0) {
                  return colors.mainG
                } else {
                  return colors.BK
                }
              },

              labelPointStyle: function (tooltipItem, context) {
                return {
                  pointStyle: 'circle',
                  rotation: 0,
                }
              },
              label: function (item) {
                return ` ${getComma(item.formattedValue * 100000)}`
              },
              // label: function (item) {
              //   return ` ${item.formattedValue * 100000}`
              // },
            },
          },
        },

        hover: {
          mode: 'index',
          intersect: false,
        },

        interaction: {
          mode: 'index',
          intersect: false,
        },
      })
    }
  }, [chartItem])

  return (
    <Container>
      {data && options && (
        <Line
          data={data}
          options={options}
          ref={chartRef}
          plugins={[shadowPlugin, verticalLinePlugin]}
        />
      )}
    </Container>
  )
}

export default OrderChart

const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
`
